// Выбор модели и глубины размышлений. Один компонент для чата (кнопка в поле ввода)
// и для настроек (поле). Модели — из Claude Code, новые помечены «новая».
import {
  contextLabel,
  effortFor,
  effortLabel,
  isNewModel,
  type ModelCatalog,
  type ModelChoice,
  type ModelInfo,
} from '@autoaff/protocol';
import { Chip, Popover } from '@autoaff/ui';
import clsx from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { choiceParts, choiceText, findModel } from '../models';

/** Чего ждать по времени на каждом уровне (замеры Opus 5.5 24.09.2026: 0,8 / 1,5 / 1,3–1,8 / ~4 / ~22 с). */
const EFFORT_WAIT: Record<string, string> = {
  low: 'Отвечает почти сразу, почти не размышляя.',
  medium: 'Первые слова — обычно через 1–2 секунды.',
  high: 'Первые слова — обычно через 1–2 секунды, на сложных вопросах дольше.',
  xhigh: 'Перед ответом думает несколько секунд.',
  max: 'Может думать до полуминуты перед ответом.',
};

export interface InheritOption {
  /** Что будет, если не выбирать своё («как по умолчанию»). */
  value: ModelChoice;
  active: boolean;
}

export function ModelPicker({
  value,
  catalog,
  onChange,
  label,
  variant = 'chip',
  prefer = 'top',
  inherit,
}: {
  value: ModelChoice;
  catalog: ModelCatalog | null;
  /** null — вернуть «как по умолчанию». Смена глубины приходит с effortOnly. */
  onChange: (choice: ModelChoice | null, effortOnly: boolean) => void;
  label: string;
  variant?: 'chip' | 'field';
  prefer?: 'top' | 'bottom';
  inherit?: InheritOption;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const parts = choiceParts(value, catalog);
  const shown = inherit?.active ? null : parts;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={variant === 'chip' ? 'mp-chip' : 'btn mp-field'}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${inherit?.active ? 'как по умолчанию' : choiceText(value, catalog)}`}
        title={variant === 'chip' ? 'Модель и глубина размышлений' : undefined}
        onClick={() => setOpen(!open)}
      >
        {variant === 'chip' ? (
          <>
            {parts.model}
            {parts.effort && <span className="dim hide-m">· {parts.effort}</span>}
          </>
        ) : (
          <span>
            {shown ? shown.model : 'Как по умолчанию'}
            <span className="dim">
              {shown ? (shown.effort ? ` · ${shown.effort}` : '') : ` · ${parts.model}`}
            </span>
          </span>
        )}
        <ChevronDown />
      </button>
      <Popover
        anchor={btn}
        open={open}
        onClose={close}
        label={label}
        prefer={prefer}
        align={variant === 'chip' ? 'start' : 'end'}
        width={360}
      >
        <ModelMenu
          value={value}
          catalog={catalog}
          inherit={inherit}
          onPick={(c) => {
            onChange(c, false);
            close();
            btn.current?.focus();
          }}
          onEffort={(effort) => onChange({ ...value, effort }, true)}
        />
      </Popover>
    </>
  );
}

function ModelMenu({
  value,
  catalog,
  inherit,
  onPick,
  onEffort,
}: {
  value: ModelChoice;
  catalog: ModelCatalog | null;
  inherit?: InheritOption;
  onPick: (c: ModelChoice | null) => void;
  onEffort: (effort: string) => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  const current = findModel(catalog, value.model);
  // Каталога ещё нет (сервис агента не подключался) — показываем хотя бы текущую модель
  const models: ModelInfo[] = catalog?.models ?? [
    {
      id: value.model,
      label: choiceParts(value).model,
      description: '',
      contextWindow: null,
      efforts: value.effort ? [value.effort] : [],
      cliDefaultEffort: null,
      firstSeenAt: null,
    },
  ];

  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const items = [...(list.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => {
      e.preventDefault();
      items[(n + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
  };

  const selectedIsInherit = inherit?.active ?? false;
  return (
    <>
      <div className="mp-sec">Модель</div>
      <div ref={list} role="listbox" aria-label="Модель" tabIndex={-1} onKeyDown={onKeyDown}>
        {inherit && (
          <button
            type="button"
            role="option"
            className="mp-opt"
            aria-selected={selectedIsInherit}
            tabIndex={selectedIsInherit ? 0 : -1}
            onClick={() => onPick(null)}
          >
            <span className="mp-name">Как по умолчанию</span>
            <span className="mp-side">{selectedIsInherit && <Check aria-hidden />}</span>
            <span className="mp-desc">Сейчас это {choiceText(inherit.value, catalog)}</span>
          </button>
        )}
        {models.map((m) => {
          const sel = !selectedIsInherit && m.id === value.model;
          const ctx = contextLabel(m.contextWindow);
          return (
            <button
              key={m.id}
              type="button"
              role="option"
              className="mp-opt"
              aria-selected={sel}
              tabIndex={sel ? 0 : -1}
              onClick={() => onPick({ model: m.id, effort: effortFor(m, value.effort) })}
            >
              <span className="mp-name">
                {m.label}
                {isNewModel(m) && <Chip tone="accent">новая</Chip>}
              </span>
              <span className="mp-side">
                {ctx && <span title="Сколько текста модель держит в голове за раз">{ctx}</span>}
                {sel && <Check aria-hidden />}
              </span>
              {m.description && <span className="mp-desc">{m.description}</span>}
            </button>
          );
        })}
      </div>
      {catalog && !current && !selectedIsInherit && (
        <p className="mp-warn">Модели {value.model} больше нет в списке Claude Code — выберите другую.</p>
      )}
      {!catalog && <p className="mp-warn">Полный список появится, когда подключится сервис агента.</p>}
      {!selectedIsInherit && current && current.efforts.length > 0 && value.effort && (
        <EffortScale model={current} value={value.effort} onChange={onEffort} />
      )}
      {!selectedIsInherit && current && current.efforts.length === 0 && (
        <div className="mp-foot">У {current.label} нет выбора глубины размышлений.</div>
      )}
    </>
  );
}

function EffortScale({
  model,
  value,
  onChange,
}: {
  model: ModelInfo;
  value: string;
  onChange: (e: string) => void;
}) {
  const id = useId();
  const idx = model.efforts.indexOf(value);
  const n = model.efforts.length;
  return (
    <div className="eff">
      <div className="eff-head">
        <span id={`${id}-l`}>Глубина размышлений</span>
        <b>{effortLabel(value)}</b>
      </div>
      <fieldset className="eff-steps" aria-labelledby={`${id}-l`}>
        {model.efforts.map((e, i) => (
          <label key={e} className={clsx('eff-step', i <= idx && 'on')} title={effortLabel(e)}>
            <input
              type="radio"
              className="sr-only"
              name={id}
              value={e}
              checked={e === value}
              onChange={() => onChange(e)}
              aria-label={effortLabel(e)}
            />
            <i style={{ height: `${n > 1 ? 25 + (75 * i) / (n - 1) : 100}%` }} />
          </label>
        ))}
      </fieldset>
      <p className="eff-hint">
        {EFFORT_WAIT[value] ? `${EFFORT_WAIT[value]} ` : ''}Чем выше, тем продуманнее ответ и тем быстрее
        тратится лимит подписки.
      </p>
    </div>
  );
}
